import {
  Component, EventEmitter, Input, Output,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl } from '@angular/forms';
import { EnhancedFormGroup } from './enhanced-form-group';
import { BaseModel } from '../../models/basemodel';
import { AbstractDataService } from '../abstractdata.service';

@Component({
    template: '',
})
export abstract class SaveableFormComponent<T extends BaseModel> {
  @Input()
  set editable(val: boolean) {
    this._editable = val;
    this.checkEnabled();
  }

  get editable() {
    return this._editable;
  }

  @Input() value!: T;

  form!: EnhancedFormGroup<{
    [key: string]: FormControl<
      string | number | boolean | Array<string | number | boolean> | undefined | null
    >;
  }>;

  @Output() closeEvent = new EventEmitter<void>();

  @Output() loadingEvent = new EventEmitter<boolean>();

  @Output() validityChange = new EventEmitter<boolean>();

  error?: HttpErrorResponse;

  private _editable = true;
  protected abstract dataService: AbstractDataService<T>;

  onSave(): void {
    if (this.form.pristine && this.form.valid) {
      this.closeEvent.emit();
      return;
    }
    if (this.form.valid) {
      this.error = undefined;
      this.loadingEvent.emit(true);
      this.value = Object.assign(this.value, this.form.value);
      this.form.disable();
      this.applyCustomValues();
      this.dataService.createOrUpdate(this.value).subscribe({
        next: (value) => {
          this.value = value;
          this.closeEvent.emit();
          this.form.enable();
          this.form.markAsPristine();
          this.loadingEvent.emit(false);
        },
        error: (response) => {
          this.error = response;
          this.form.enable();
          this.loadingEvent.emit(false);
        },
      });
    } else {
      this.form.markAllAsTouched();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  applyCustomValues(): void { }

  protected abstract setFormGroup(): void;

  setValue(value: T): void {
    this.error = undefined;
    this.value = value;
    this.setFormGroup();
    this.checkEnabled();
    this.afterSetup();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this.form as any).updateValueAndValidity(false, true);
    this.validityChange.emit(this.form.valid);
    Object.keys(this.form.controls).forEach((key) => {
      if (this.editable) {
        this.form.controls[key].enable();
      } else {
        this.form.controls[key].disable();
      }
    });

    // hack-fix: for some reason form.valid is not correctly set for a split second (even though the form is valid)
    // setTimeout(0) fixes this, but not sure why
    this.form.valueChanges.subscribe(() => {
      setTimeout(() => {
        this.validityChange.emit(this.form.valid);
      }, 0);
    });
  }

  private checkEnabled(): void {
    if (this._editable) {
      this.form.enable();
    } else {
      this.form.disable();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected afterSetup(): void { }
}
